import * as Yup from "yup";
import { extractApiError } from "./apiError";

/**
 * An RFQ opens and closes at the instant the buyer chose: a day and a time read
 * in Argentina, whatever the browser's own zone. The backend applies the same
 * rules (BiddingWindowRules) and shows the same zone.
 */
export const BIDDING_TIME_ZONE = "America/Argentina/Buenos_Aires";

// Argentina has kept UTC-3 all year, with no daylight saving, since 2009.
const ARGENTINA_OFFSET = "-03:00";

const datePartsIn = (instant: Date) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BIDDING_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  const part = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return {
    date: `${part("year")}-${part("month")}-${part("day")}`,
    time: `${part("hour")}:${part("minute")}`,
    display: `${part("day")}/${part("month")}/${part("year")} ${part("hour")}:${part("minute")}`,
  };
};

/** Today's date in Argentina, "YYYY-MM-DD" — the `min` of a bidding date input. */
export const todayInArgentina = (now: Date = new Date()): string =>
  datePartsIn(now).date;

/** "2026-10-20" + "18:30" in Argentina → the ISO instant the API expects. */
export const toArgentinaInstant = (date: string, time: string): string =>
  new Date(`${date}T${time}:00${ARGENTINA_OFFSET}`).toISOString();

/** The Argentine day ("YYYY-MM-DD") of an instant from the API. */
export const argentinaDateOf = (instant: string): string =>
  datePartsIn(new Date(instant)).date;

/** The Argentine time ("HH:mm") of an instant from the API. */
export const argentinaTimeOf = (instant: string): string =>
  datePartsIn(new Date(instant)).time;

/**
 * An RFQ's opening or closing as "dd/MM/yyyy HH:mm" in Argentina. Falls back to
 * the bare day for anything that arrives without an instant.
 */
export const formatBiddingDateTime = (
  instant?: string | null,
  fallbackDate?: string | null
): string => {
  if (instant) return datePartsIn(new Date(instant)).display;
  if (!fallbackDate) return "";
  const [year, month, day] = fallbackDate.slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : fallbackDate;
};

/** A bidding window as the launch forms hold it: days and "HH:mm" times. */
export type BiddingWindowInput = {
  startDateValue?: string;
  startTimeValue?: string;
  endDateValue?: string;
  endTimeValue?: string;
};

export type BiddingWindowErrors = {
  startTime?: string;
  endTime?: string;
};

export const BIDDING_WINDOW_ERRORS = {
  closeTimeRequired: "tenders.form.closeTimeRequired",
  openTimeRequired: "tenders.form.openTimeRequired",
  closeInPast: "tenders.form.closeInPast",
  closeBeforeOpen: "tenders.form.closeBeforeOpen",
};

/** A launch with a start day after today is scheduled: it needs its opening time. */
export const isScheduledStart = (startDate?: string, now: Date = new Date()) =>
  !!startDate && startDate > todayInArgentina(now);

const closingInstant = (w: BiddingWindowInput) =>
  w.endDateValue && w.endTimeValue
    ? new Date(toArgentinaInstant(w.endDateValue, w.endTimeValue))
    : null;

const openingInstant = (w: BiddingWindowInput) =>
  w.startDateValue && w.startTimeValue
    ? new Date(toArgentinaInstant(w.startDateValue, w.startTimeValue))
    : null;

/**
 * "Que sea obligatorio elegir horario de cierre. Y en caso de que se programe,
 * que sea obligatorio el horario de apertura." Only judged once the day it
 * belongs to has been picked.
 */
type WindowTestContext = { options: { context?: unknown } };

export const biddingWindowSchema = (now: Date = new Date()) => {
  const windowOf = (context: WindowTestContext) =>
    (context.options.context ?? {}) as BiddingWindowInput;
  return Yup.object({
    startTime: Yup.string().test(
      "opening-time-when-scheduled",
      BIDDING_WINDOW_ERRORS.openTimeRequired,
      (_: unknown, context: WindowTestContext) => {
        const w = windowOf(context);
        return !isScheduledStart(w.startDateValue, now) || !!w.startTimeValue;
      }
    ),
    endTime: Yup.string()
      .test(
        "closing-time-required",
        BIDDING_WINDOW_ERRORS.closeTimeRequired,
        (_: unknown, context: WindowTestContext) => {
          const w = windowOf(context);
          return !w.endDateValue || !!w.endTimeValue;
        }
      )
      .test(
        "closing-ahead",
        BIDDING_WINDOW_ERRORS.closeInPast,
        (_: unknown, context: WindowTestContext) => {
          const closing = closingInstant(windowOf(context));
          return !closing || closing > now;
        }
      )
      .test(
        "closing-after-opening",
        BIDDING_WINDOW_ERRORS.closeBeforeOpen,
        (_: unknown, context: WindowTestContext) => {
          const w = windowOf(context);
          const closing = closingInstant(w);
          const opening = openingInstant(w);
          return !closing || !opening || closing > opening;
        }
      ),
  });
};

/** The i18n key of each time field's problem, if it has one. */
export const getBiddingWindowErrors = (
  window: BiddingWindowInput,
  now: Date = new Date()
): BiddingWindowErrors => {
  try {
    biddingWindowSchema(now).validateSync(
      {},
      { abortEarly: false, context: window }
    );
    return {};
  } catch (error) {
    if (!(error instanceof Yup.ValidationError)) throw error;
    const { inner: failures } = error as {
      inner: { path?: string; message: string }[];
    };
    return failures.reduce<BiddingWindowErrors>(
      (errors, failure) =>
        failure.path && !(failure.path in errors)
          ? { ...errors, [failure.path]: failure.message }
          : errors,
      {}
    );
  }
};

/** Whether the window is complete and valid: both days, the times it needs. */
export const isBiddingWindowValid = (
  window: BiddingWindowInput,
  now: Date = new Date()
) =>
  !!window.startDateValue &&
  !!window.endDateValue &&
  !!window.endTimeValue &&
  Object.keys(getBiddingWindowErrors(window, now)).length === 0;

/** The instants sent with a launch; an opening left out means "open now". */
export const biddingInstants = (window: BiddingWindowInput) => ({
  opensAt:
    window.startDateValue && window.startTimeValue
      ? toArgentinaInstant(window.startDateValue, window.startTimeValue)
      : undefined,
  closesAt:
    window.endDateValue && window.endTimeValue
      ? toArgentinaInstant(window.endDateValue, window.endTimeValue)
      : undefined,
});

/**
 * The message key the backend used to answer (409) when an offer arrived after the RFQ closed.
 * Transitional: the backend now sends the `ORDER_CLOSED` code, which is checked first.
 */
export const ORDER_CLOSED_ERROR = "offer.orderClosed";

/** The backend error code for a request refused because the RFQ had already closed. */
export const ORDER_CLOSED_CODE = "ORDER_CLOSED";

const CLOSED_ORDER_STATES = ["CLOSED", "SUCCEED", "CANCELLED"];

/**
 * Whether an RFQ no longer takes offers: closed, awarded or cancelled, or its
 * closing instant has passed (the state itself moves once a minute).
 */
export const isBiddingClosed = (
  order?: { state?: string | null; closesAt?: string | null } | null,
  now: Date = new Date()
): boolean =>
  !!order &&
  (CLOSED_ORDER_STATES.includes(order.state ?? "") ||
    (!!order.closesAt && now >= new Date(order.closesAt)));

/**
 * Whether a failed request was refused because the RFQ had already closed. Code-first
 * (`ORDER_CLOSED`); the legacy 409 + `offer.orderClosed` message is kept as a fallback.
 */
export const isOrderClosedError = (error: unknown): boolean => {
  if (extractApiError(error).code === ORDER_CLOSED_CODE) return true;
  const response = (error as { response?: { status?: number; data?: { message?: string } } })
    ?.response;
  return response?.status === 409 && response?.data?.message === ORDER_CLOSED_ERROR;
};

/**
 * The i18n key to tell the supplier why an offer request failed. Prefer `openError(error)` from
 * `useNotification()`, which shows the backend's own translated error; this stays for callers
 * that need a key.
 */
export const offerErrorKey = (error: unknown): string =>
  isOrderClosedError(error) ? ORDER_CLOSED_ERROR : "newMaterialForm.error";
